/**
 * The shared entity-kind filter axis for Tapahtumaloki / Havainnot / Suositukset
 * (docs/spec/concepts.md § Shared entity-kind filter, story #964/#966/#969). English kinds
 * under the hood, Finnish UI labels only — never named `PerspectiveId`, composes with (never
 * replaces) each surface's own axes (ScopeBar scope, activity lens categories, `Finding.category`).
 */
import { detailString } from "@/components/control-tower/activity-feed";
import type {
  ActivityEntityRef,
  ActivityFeedRow,
  ActivityKind,
} from "@/lib/api/activity";
import type {
  Finding,
  FindingCategory,
  StrategyField,
} from "@/lib/api/building";
import type { FindingsPerspective } from "@/lib/api/useScopedFindings";

export type EntityKind = "signal" | "equipment" | "zone";

export const ENTITY_KINDS: readonly EntityKind[] = [
  "signal",
  "equipment",
  "zone",
];

export const ENTITY_KIND_LABEL_FI: Record<EntityKind, string> = {
  signal: "Datalaatu",
  equipment: "Talotekniikka",
  zone: "Vyöhykkeet",
};

// Havainnot mapping: `data_quality` findings are always source_type "equipment" but represent
// the data axis, so category wins before source_type — reuses the existing category field
// rather than inventing a fourth source_type.
export function entityKindForFinding(
  finding: Pick<Finding, "source_type" | "category">,
): EntityKind | null {
  if (finding.category === "data_quality") return "signal";
  // `configuration` findings are always source_type "equipment" in v1 (story #1298), so this
  // falls through to the source_type branch below — named explicitly so the mapping is visible
  // rather than an accident of fallback order.
  if (finding.category === "configuration") return "equipment";
  if (finding.source_type === "zone") return "zone";
  if (finding.source_type === "equipment") return "equipment";
  return null;
}

// Tapahtumaloki mapping: `kind` first (the more specific signal); `finding_event` rows apply the
// same data_quality-first precedence `entityKindForFinding` uses above —
// `detail.finding_category === "data_quality"` → `signal`, otherwise `where.type` zone/equipment.
// Building's activity read service exposes `detail.finding_category` precisely so this
// precedence can apply here too (§2.2 "Field mapping" — the v1 Signaali asymmetry is retired).
export function entityKindForActivityRow(row: {
  kind: ActivityKind;
  where: Pick<ActivityEntityRef, "type">;
  detail: ActivityFeedRow["detail"];
}): EntityKind | null {
  switch (row.kind) {
    case "equipment_added":
      return "equipment";
    case "capability_wired":
    case "syota_submitted":
      return "signal";
    case "finding_event":
      if (detailString(row.detail, "finding_category") === "data_quality")
        return "signal";
      if (row.where.type === "zone") return "zone";
      if (row.where.type === "equipment") return "equipment";
      return null;
    // A learned parameter or inference settling, and a recipe's outcome, are about one equipment
    // item (Building records both against it).
    case "learning_state_changed":
    case "recipe_applied":
      return "equipment";
    // A schedule pause/resume is a building/agent-level action, not the signal/equipment/zone
    // Datalaatu/Talotekniikka/Vyöhykkeet axis (story #1633) — no entity kind applies.
    case "building_created":
    case "kb_tick_finished":
    case "agent_schedule_changed":
      return null;
  }
}

// Same rule as `entityKindForFinding`'s category branch, usable before any Finding is fetched —
// e.g. Analyysi's track selector (`Finding.category` fault/comfort per track, story #850) needs a
// default entity kind for its embedded Havainnot before the list has loaded a single row.
export function entityKindForFindingCategory(
  category: FindingCategory,
): EntityKind | null {
  if (category === "data_quality") return "signal";
  if (category === "comfort") return "zone";
  if (category === "fault") return "equipment";
  if (category === "configuration") return "equipment";
  return null; // "opportunity" (Optimointi's Mahdollisuus track) has no fixed entity kind
}

// Suositukset mapping: strategy fields split by the comfort/zone vs. building-equipment axis
// they already encode — no StrategyField maps to signal/data_point in v1.
const ZONE_STRATEGY_FIELDS: ReadonlySet<StrategyField> = new Set([
  "comfort_temp_min",
  "comfort_temp_max",
  "co2_limit_ppm",
]);
const EQUIPMENT_STRATEGY_FIELDS: ReadonlySet<StrategyField> = new Set([
  "consumption_kwh_m2_year",
  "peak_power_cap_kw",
  "savings_goal_eur_year",
]);

export function entityKindForStrategyField(
  field: StrategyField,
): EntityKind | null {
  if (ZONE_STRATEGY_FIELDS.has(field)) return "zone";
  if (EQUIPMENT_STRATEGY_FIELDS.has(field)) return "equipment";
  return null;
}

// Missä chip <-> Havainnot query (issue #1544): the one chip<->query mapping, colocated with
// entityKindForFindingCategory. The Missä row is the single narrowing chrome on /findings — a
// lens arrival (LensTopicSection's HavainnotHeaderLink) pre-selects the chip that matches the
// perspective it deep-links with, and selecting a chip writes the same perspective back into the
// URL through useScopedNavigate. Forward keeps the Datalaatu chip on `category`, never
// `source_type: "signal"` (that source_type value doesn't exist).
export function findingsPerspectiveForEntityKind(
  kind: EntityKind | "all",
): FindingsPerspective {
  switch (kind) {
    case "signal":
      return { category: "data_quality" };
    case "equipment":
      return { sourceType: "equipment" };
    case "zone":
      return { sourceType: "zone" };
    case "all":
      return {};
  }
}

// Inverse of `findingsPerspectiveForEntityKind`, for lighting the right chip on arrival. Reuses
// `entityKindForFindingCategory` for the category branch (data_quality wins before source_type,
// same precedence as `entityKindForFinding`) so a lens's `category=fault` link still lights
// Talotekniikka without the lens itself sending a `source` param.
export function entityKindForFindingsPerspective(
  perspective: FindingsPerspective,
): EntityKind | "all" {
  const { category, sourceType } = perspective;
  if (category) {
    const kind = entityKindForFindingCategory(category);
    if (kind) return kind;
  }
  if (sourceType === "zone") return "zone";
  if (sourceType === "equipment") return "equipment";
  return "all";
}
